import type { AgentMentionedToolItem } from '@cotera/api';
import { useMemo } from 'react';
import {
  useWatchableValue,
  Watchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';

/**
 * A tool the agent `@`-mentions. Derived from the endpoint's response item
 * (`AgentMentionedToolItem` in `@cotera/api`) rather than hand-defined — narrow
 * to what the node UI shows. Widen this `Pick` to surface more (e.g.
 * `canExecute`) without redefining the field types.
 */
export type AgentTool = Pick<AgentMentionedToolItem, 'key' | 'name'>;

export type AgentNodeConfig = {
  /** Tools the agent @-mentions (and can therefore call). */
  readonly tools: AgentTool[];
  /**
   * The agent's current input type as an `Ty.displayTy` string, or null while
   * loading / when no agent is set.
   */
  readonly inputShape: string | null;
  readonly editorState: unknown | null;
  readonly agentKey: string | null;
  /** Live model id, or null while loading. */
  readonly model: string | null;
  readonly description: string | null;
};

const EMPTY: AgentNodeConfig = {
  tools: [],
  inputShape: null,
  editorState: null,
  agentKey: null,
  model: null,
  description: null,
};

/**
 * An agent's live workflow-node config — editor state, `@`-mentioned toolset,
 * and input shape. Backed by the per-agent `ResourceUpdated` event watchable.
 */
export function useAgentNodeConfig(
  agentId: string | undefined
): AgentNodeConfig {
  const factory = useWatchablesFactory();
  const client = useTenantedClient();
  const enabled = agentId !== undefined && agentId !== '';

  const watchable = useMemo<ReadonlyWatchable<AgentNodeConfig>>(() => {
    if (!enabled || agentId === undefined) {
      return Watchable.fromValue(EMPTY);
    }
    return factory.makeAgentNodeConfigWatchable({ agentId, client });
  }, [factory, client, agentId, enabled]);

  return useWatchableValue(watchable);
}
